// EntitySearchDialog.jsx
// Federated search for Wikidata + custom (D-) entities via GET /api/entities/search

import React, {useEffect, useRef, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {formatEntityMentionCounts, searchKbEntities} from '../../../utils/api/kbEntitySearch';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {isLocalEntityRef} from '../../../utils/entityLinks';
import CreateCustomEntityDialog from '../../../features/knowledgeBase/components/CreateCustomEntityDialog';

const RESULT_AVATAR_SIZE = 40;

/** @param {{ id?: string, entityRef?: string, imageUrl?: string|null, image?: string|null }} entity */
function resolveSearchHitAvatarUrl(entity) {
    const id = entity?.id || entity?.entityRef || '';
    const imageUrl = entity?.imageUrl || entity?.image || null;

    if (isLocalEntityRef(id)) {
        return imageUrl || `/api/entities/${encodeURIComponent(id)}/avatar`;
    }

    return resolveEntityAvatarUrl({
        wikidataId: id,
        imageUrl,
    });
}

/** @param {string|null|undefined} label */
function entityAvatarInitial(label) {
    return String(label || '?').slice(0, 1).toUpperCase();
}

function isCustomHit(entity) {
    return isLocalEntityRef(entity?.id || entity?.entityRef || '');
}

function isKbHit(entity) {
    return Boolean(entity?.local) && !isCustomHit(entity);
}

function isWikidataOnlyHit(entity) {
    return !isCustomHit(entity) && !entity?.local;
}

/** Origin label shown on every picker row. */
function sourceChipLabel(entity) {
    if (isCustomHit(entity)) {
        return 'Custom';
    }
    if (entity?.local) {
        return 'Knowledge Base';
    }
    return 'Wikidata';
}

function orderEntitySearchHits(hits) {
    const custom = [];
    const kb = [];
    const wikidata = [];
    for (const entity of hits) {
        if (isCustomHit(entity)) {
            custom.push(entity);
        } else if (isKbHit(entity)) {
            kb.push(entity);
        } else {
            wikidata.push(entity);
        }
    }
    return [...custom, ...kb, ...wikidata];
}

function entityLinkCountsLabel(entity) {
    return formatEntityMentionCounts(entity);
}

function ResultSectionHeader({children}) {
    return (
        <ListSubheader
            disableSticky
            sx={{
                bgcolor: 'transparent',
                lineHeight: 2.25,
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'text.secondary',
            }}
        >
            {children}
        </ListSubheader>
    );
}

function EntityResultRow({
    entity,
    index,
    highlighted,
    optionRefs,
    onHighlight,
    onSelect,
}) {
    const avatarUrl = resolveSearchHitAvatarUrl(entity);
    const sourceLabel = sourceChipLabel(entity);

    return (
        <ListItem disablePadding>
            <ListItemButton
                ref={(el) => {
                    optionRefs.current[index] = el;
                }}
                selected={highlighted}
                aria-selected={highlighted}
                onMouseEnter={() => onHighlight(index)}
                onClick={() => onSelect(entity)}
                sx={{gap: 1.25}}
            >
                <ListItemAvatar sx={{minWidth: RESULT_AVATAR_SIZE}}>
                    <Avatar
                        variant="rounded"
                        src={avatarUrl || undefined}
                        alt=""
                        aria-hidden
                        slotProps={{
                            img: {
                                draggable: false,
                                referrerPolicy: 'strict-origin-when-cross-origin',
                            },
                        }}
                        sx={{
                            width: RESULT_AVATAR_SIZE,
                            height: RESULT_AVATAR_SIZE,
                            borderRadius: 1.5,
                            bgcolor: 'grey.200',
                            fontSize: '1rem',
                            fontWeight: 600,
                        }}
                    >
                        {entityAvatarInitial(entity.label)}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography component="span" variant="body1">
                                {entity.label}
                            </Typography>
                            {sourceLabel ? (
                                <Chip
                                    label={sourceLabel}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        height: 20,
                                        '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'},
                                    }}
                                />
                            ) : null}
                        </Stack>
                    }
                    secondary={
                        <>
                            {entity.description && (
                                <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{display: 'block'}}
                                >
                            {entity.description}
                            </Typography>
                            )}
                            <Typography
                                component="span"
                                variant="caption"
                                color="text.disabled"
                            >
                                {entity.id}
                                {entityLinkCountsLabel(entity) ? ` · ${entityLinkCountsLabel(entity)}` : ''}
                            </Typography>
                        </>
                    }
                />
            </ListItemButton>
        </ListItem>
    );
}

/**
 * Entity search dialog for linking mentions to Wikidata or custom KB entities.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onSelect - receives normalized entity {id, label, description, local, ...}
 * @param {string} [props.initialQuery]
 */
export default function EntitySearchDialog({open, onClose, onSelect, initialQuery = ''}) {
    const [query, setQuery] = useState(initialQuery);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pendingEntity, setPendingEntity] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const searchTimerRef = useRef(null);
    const inputRef = useRef(null);
    const optionRefs = useRef([]);

    useEffect(() => {
        if (open) {
            setQuery(initialQuery || '');
            setHighlightedIndex(-1);
        }
    }, [open, initialQuery]);

    useEffect(() => {
        if (!open && pendingEntity && onSelect) {
            onSelect(pendingEntity);
            setPendingEntity(null);
        }
    }, [open, pendingEntity, onSelect]);

    useEffect(() => {
        if (!open) return;

        const timer = window.setTimeout(() => {
            try {
                inputRef.current?.focus({preventScroll: true});
            } catch (error) {
                inputRef.current?.focus();
            }
        }, 0);

        return () => window.clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        if (!query || !query.trim()) {
            setResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        const timer = setTimeout(async () => {
            try {
                const {results: hits} = await searchKbEntities(query, 20, {preferWikidata: true});
                setResults(orderEntitySearchHits(Array.isArray(hits) ? hits : []));
            } catch (err) {
                console.error('Error searching entities:', err);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);

        searchTimerRef.current = timer;

        return () => {
            clearTimeout(timer);
        };
    }, [query, open]);

    useEffect(() => {
        if (open) {
            setHighlightedIndex(-1);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        if (!results.length) {
            setHighlightedIndex(-1);
            return;
        }

        setHighlightedIndex((prev) => {
            if (prev < 0) return 0;
            return Math.min(prev, results.length - 1);
        });
    }, [results, open]);

    useEffect(() => {
        if (highlightedIndex < 0) return;
        const optionEl = optionRefs.current[highlightedIndex];
        optionEl?.scrollIntoView?.({block: 'nearest'});
    }, [highlightedIndex]);

    const handleClose = (event, reason) => {
        setQuery('');
        setResults([]);
        setLoading(false);
        onClose?.(event, reason);
    };

    const handleSelect = (entity) => {
        setPendingEntity(entity);
        handleClose();
    };

    const handleCustomEntityCreated = (entity) => {
        handleSelect(entity);
    };

    const showCreateButton = Boolean(query.trim()) && !loading && !results.length;
    const customHits = results.filter(isCustomHit);
    const kbHits = results.filter(isKbHit);
    const wikidataHits = results.filter(isWikidataOnlyHit);

    const handleInputKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            handleClose(event, 'escapeKeyDown');
            return;
        }

        if (!results.length) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((prev) => (prev + 1) % results.length);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((prev) => (prev <= 0 ? results.length - 1 : prev - 1));
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const index = highlightedIndex >= 0 ? highlightedIndex : 0;
            const entity = results[index];
            if (entity) {
                handleSelect(entity);
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableScrollLock
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" fontWeight={600}>Link to entity</Typography>
                    <Tooltip title="Close">
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{pt: '8px !important'}}>
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    size="small"
                    placeholder="Search Wikidata or Knowledge Base…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon/>
                                </InputAdornment>
                            ),
                        }
                    }}
                    sx={{mb: 1}}
                />

                <Divider sx={{mb: 1}}/>

                <Box sx={{minHeight: 240, maxHeight: 360, overflow: 'auto'}}>
                    {loading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4}}>
                            <CircularProgress size={32}/>
                        </Box>
                    ) : results.length > 0 ? (
                        <List disablePadding>
                            {customHits.length > 0 ? (
                                <>
                                    <ResultSectionHeader>Custom</ResultSectionHeader>
                                    {customHits.map((entity, i) => (
                                        <EntityResultRow
                                            key={entity.id}
                                            entity={entity}
                                            index={i}
                                            highlighted={i === highlightedIndex}
                                            optionRefs={optionRefs}
                                            onHighlight={setHighlightedIndex}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </>
                            ) : null}
                            {kbHits.length > 0 ? (
                                <>
                                    <ResultSectionHeader>Knowledge Base</ResultSectionHeader>
                                    {kbHits.map((entity, i) => {
                                        const index = customHits.length + i;
                                        return (
                                            <EntityResultRow
                                                key={entity.id}
                                                entity={entity}
                                                index={index}
                                                highlighted={index === highlightedIndex}
                                                optionRefs={optionRefs}
                                                onHighlight={setHighlightedIndex}
                                                onSelect={handleSelect}
                                            />
                                        );
                                    })}
                                </>
                            ) : null}
                            {wikidataHits.length > 0 ? (
                                <>
                                    <ResultSectionHeader>Wikidata</ResultSectionHeader>
                                    {wikidataHits.map((entity, i) => {
                                        const index = customHits.length + kbHits.length + i;
                                        return (
                                            <EntityResultRow
                                                key={entity.id}
                                                entity={entity}
                                                index={index}
                                                highlighted={index === highlightedIndex}
                                                optionRefs={optionRefs}
                                                onHighlight={setHighlightedIndex}
                                                onSelect={handleSelect}
                                            />
                                        );
                                    })}
                                </>
                            ) : null}
                        </List>
                    ) : query ? (
                        <Stack spacing={1.5} alignItems="center" sx={{py: 4, px: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                No entities found
                            </Typography>
                            {showCreateButton ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon/>}
                                    onClick={() => setCreateOpen(true)}
                                >
                                    Create custom entity
                                </Button>
                            ) : null}
                        </Stack>
                    ) : (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4}}>
                            <Typography variant="body2" color="text.secondary">
                                Start typing to search for entities
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <CreateCustomEntityDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSuccess={handleCustomEntityCreated}
                initialName={query.trim()}
            />
        </Dialog>
    );
}
